import { useCallback, useState, type ReactNode } from "react";
import { ConfirmModal, type ChangeValue } from "./ConfirmModal";
import { needsConfirm, type Risk } from "./risk";
import { useAdminT } from "./useAdminT";

export interface ConfirmTarget {
  name: string;
  risk: Risk;
}

interface Pending {
  target: ConfirmTarget;
  before: ChangeValue;
  after: ChangeValue;
  body?: ReactNode;
  run: () => void;
}

/**
 * The one confirm rule: risk "none" runs the mutation at once; "caution" and
 * "danger" open a before → after modal and run only after Apply.
 * Render `modal` once next to the caller.
 */
export function useConfirmedChange(): {
  confirmChange: (
    target: ConfirmTarget,
    before: ChangeValue,
    after: ChangeValue,
    run: () => void,
    body?: ReactNode,
  ) => void;
  modal: ReactNode;
} {
  const t = useAdminT();
  const [pending, setPending] = useState<Pending | null>(null);

  const confirmChange = useCallback(
    (target: ConfirmTarget, before: ChangeValue, after: ChangeValue, run: () => void, body?: ReactNode) => {
      if (!needsConfirm(target.risk)) {
        run();
        return;
      }
      setPending({ target, before, after, body, run });
    },
    [],
  );

  const modal = (
    <ConfirmModal
      open={pending !== null}
      title={pending ? t("shell.confirm.title", { name: pending.target.name }) : ""}
      body={pending?.body}
      before={pending?.before}
      after={pending?.after}
      risk={pending?.target.risk}
      onClose={() => setPending(null)}
      onConfirm={() => {
        const p = pending;
        setPending(null);
        p?.run();
      }}
    />
  );
  return { confirmChange, modal };
}
